import { faSdg3 } from '@awesome.me/kit-335a9e77a1/icons/kit/custom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChartCard, InfoTooltip, LabelText, VerticalBar } from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { getReferenceLines } from '../-helpers/getReferenceLines';
import { getTarget } from '../-helpers/getSDGTargetData';
import { useChartData } from '../-hooks/useChartData';

export const Sdg_IncreasingMedicalAccessFragment = gql(/* GraphQL */ `
  fragment Sdg_IncreasingMedicalAccessFragment on HealthcareIndicators {
    patientsReceivingNewAccess_pacePct {
      average
      median
      n
      isDataSuppressed
    }
  }
`);

type IncreasingMedicalAccessProps = {
  className?: string;
  indicators: FragmentType<typeof Sdg_IncreasingMedicalAccessFragment>;
  viewerIndicators?: FragmentType<typeof Sdg_IncreasingMedicalAccessFragment>;
  region: string;
};

export function IncreasingMedicalAccess({
  indicators,
  viewerIndicators,
  region,
  className,
}: IncreasingMedicalAccessProps) {
  const { t } = useTranslation();

  const benchmarkFragment = useFragment(Sdg_IncreasingMedicalAccessFragment, indicators);
  const viewerFragment = useFragment(Sdg_IncreasingMedicalAccessFragment, viewerIndicators);

  const benchmarkData = benchmarkFragment.patientsReceivingNewAccess_pacePct;
  const viewerData = viewerFragment?.patientsReceivingNewAccess_pacePct;

  const sdgTarget = useMemo(() => getTarget('increasingMedicalAccess', region), [region]);
  const referenceLines = getReferenceLines(sdgTarget);

  const { groups, data, yAxisProps } = useChartData({
    viewerData,
    benchmarkData,
    referenceLineValues: referenceLines.map((x) => x.value),
  });

  const titleTooltip = t('increasingMedicalAccess_titleTooltip_sdg');

  return (
    <ChartCard className={className}>
      <ChartCard.Header>
        <div className="flex items-center gap-1.5">
          <FontAwesomeIcon icon={faSdg3} size="2x" className="text-sdg-3" />
          <LabelText>{t('sdgsTargeted_optionLabel_3').replace('3', '3.b.1, 3.b.3')}</LabelText>
        </div>
        <ChartCard.Title>
          {t('increasingMedicalAccess_title')}
          {titleTooltip && <InfoTooltip>{titleTooltip}</InfoTooltip>}
        </ChartCard.Title>
      </ChartCard.Header>
      <ChartCard.Body>
        <VerticalBar groups={groups} data={data}>
          <VerticalBar.Chart yAxis={yAxisProps} referenceLines={referenceLines} />
          <VerticalBar.Legend className="flex items-center justify-center gap-4" />
        </VerticalBar>
      </ChartCard.Body>
    </ChartCard>
  );
}
